import React, { useState, useEffect, forwardRef } from "react";
import WorkflowBuilder from "./builder_container";
import axiosInstance from "../../../../axiosInstance";
import Typography from 'apollo-react/components/Typography';
import Close from 'apollo-react-icons/Close';
import ThumbsUpFilled from 'apollo-react-icons/ThumbsUpFilled';
import Redo from 'apollo-react-icons/Redo';
import IconButton from 'apollo-react/components/IconButton';

const WorkflowInit = forwardRef(({ preJsonData }, ref) => {
  const [approvalTypes, setApprovalTypes] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);

  // Parse Json if it's a string
  useEffect(() => {
    const parsedPreJsonData = preJsonData
      ? typeof preJsonData === "string"
        ? JSON.parse(preJsonData)
        : preJsonData
      : null;

    // Transform nodes to have JSX labels
    // If Json received convert labels to Typography elements

    const iconMapping = {
      ThumbsUpFilled: ThumbsUpFilled,
      Close: Close,
      Redo: Redo,
    };

    // Helper function to derive category from node properties (for backward compatibility with old templates)
    const deriveCategory = (node) => {
      // If category exists, use it
      if (node.category) return node.category;
      if (node.data?.category) return node.data.category;
      
      // Derive from node type and properties
      if (node.type === 'input') return 'Start';
      if (node.type === 'output' || node.data?.isEndNode) return 'End';
      
      // For FormStage nodes, check stage_type_id
      if (node.type === 'FormStage') {
        const stageTypeId = node.data?.formConfig?.stage_type_id;
        if (stageTypeId === 1) return 'Submission';
        if (stageTypeId === 3) return 'Review';
        return 'Stage'; // Default for FormStage
      }
      
      // For approval child nodes
      if (node.scope === 'ReviewChildNode' || node.className === 'child-node') {
        const approvalTypeId = node.data?.approvalTypeId;
        if (approvalTypeId === 1 || node.type === 'app') return 'child-app';
        if (approvalTypeId === 2 || node.type === 'rej') return 'child-rej';
        if (approvalTypeId === 3 || node.type === 'sbk') return 'child-sbk';
      }
      
      // Fallback
      return node.type;
    };

    
    const transformedNodes = parsedPreJsonData?.nodes?.map((node) => {
      const derivedCategory = deriveCategory(node);
      
      return {
        ...node,
        category: derivedCategory, // Ensure category is set at top level
        data: {
          ...node.data,
          category: derivedCategory, // Also set in data for React Flow compatibility
          label: (
            <Typography variant="subtitle1">
              {node.data.name ? node.data.name : "Default Label"}
            </Typography>
          ),
        },
      };
    }) || [
      {
        id: "start",
        type: "input",
        data: { 
          label: (<Typography variant="subtitle1">Start Flow</Typography>), 
          name: "Start Flow",
          category: "Start" // Store in data for React Flow compatibility
        },
        category: "Start",
        position: { x: 350, y: 150 },
      },
    ];

    setNodes(transformedNodes);
    setEdges(parsedPreJsonData ? parsedPreJsonData?.edges : []);
  }, [preJsonData]);

  // Get Approval types
  useEffect(() => {
    async function fetchData() {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_SERVER_URL}/api/approval_types`,
          { withCredentials: true }
        );

        setApprovalTypes(response?.data);
      } catch (error) {
        // Error fetching approval types
      }
    }

    fetchData();
  }, []);

  return (
    <WorkflowBuilder
      ref={ref}
      approvalTypes={approvalTypes}
      sentNodes={nodes}
      sentEdges={edges}
    />
  );
});

export default WorkflowInit;
